// 2. External libraries
import { useQuery, useQueryClient } from '@tanstack/react-query';

// 4. Type imports
import type { UseQueryResult } from '@tanstack/react-query';
import type { ConnectionResponse, TopicResponse } from '~/generated/api/model';

// 5. Custom hooks and utilities
import { getTopicByIdV1TopicsTopicIdGet } from '~/generated/api/topics/topics';
import { queryKeys } from './queryKeys';

export function useTopicQuery(topicId: string | undefined): UseQueryResult<TopicResponse, Error> {
  const queryClient = useQueryClient();

  return useQuery<TopicResponse, Error, TopicResponse, readonly [string, string | undefined]>({
    queryKey: queryKeys.topic(topicId),
    // Seeds sibling caches read-only-if-absent (never overwrites existing
    // entries); consumers must not rely on seeding for freshness.
    queryFn: async () => {
      if (!topicId) throw new Error('Topic ID is required');
      const response = await getTopicByIdV1TopicsTopicIdGet(topicId);

      // Pre-populate connections list cache to avoid redundant API call
      // Only seed if not already cached to avoid overwriting pending edits
      if (response.connections && response.connections.length > 0) {
        const existingConnections = queryClient.getQueryData(
          queryKeys.connections.list(topicId, undefined)
        );
        if (!existingConnections) {
          queryClient.setQueryData(
            queryKeys.connections.list(topicId, undefined),
            response.connections
          );
        }

        // Also seed individual connection caches
        response.connections.forEach((connection: ConnectionResponse) => {
          const existingData = queryClient.getQueryData(
            queryKeys.connection(topicId, connection.id)
          );
          if (!existingData) {
            queryClient.setQueryData(queryKeys.connection(topicId, connection.id), connection);
          }
        });
      }

      return response;
    },
    enabled: !!topicId,
  });
}
